import { Vec2, GRID_SIZE, GRID_COLOR, ARENA_WIDTH, ARENA_HEIGHT } from '@mine-or-die/shared';
import { GameWindow } from '../platform/window.js';

/**
 * 2D Renderer with Tron-style neon aesthetic
 * All drawing operations work in world-space coordinates
 */
export class Renderer2D {
  private window: GameWindow;
  private ctx: CanvasRenderingContext2D;

  // Glow settings
  private glowEnabled: boolean = true;
  private glowIntensity: number = 8;

  constructor(window: GameWindow) {
    this.window = window;
    this.ctx = window.ctx;
  }

  /**
   * Begin a new frame
   */
  beginFrame(): void {
    this.window.clear();
  }

  /**
   * Begin drawing in world space
   */
  beginWorld(): void {
    this.window.beginWorldTransform();
  }

  /**
   * End drawing in world space
   */
  endWorld(): void {
    this.window.endWorldTransform();
  }

  /**
   * Draw the scrolling background grid
   */
  drawGrid(cameraX: number = 0, cameraY: number = 0): void {
    const ctx = this.ctx;
    ctx.strokeStyle = GRID_COLOR;
    ctx.lineWidth = 1;

    // Calculate grid offset based on camera
    const offsetX = -(cameraX % GRID_SIZE);
    const offsetY = -(cameraY % GRID_SIZE);

    // Vertical lines
    for (let x = offsetX; x <= ARENA_WIDTH; x += GRID_SIZE) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, ARENA_HEIGHT);
      ctx.stroke();
    }

    // Horizontal lines
    for (let y = offsetY; y <= ARENA_HEIGHT; y += GRID_SIZE) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(ARENA_WIDTH, y);
      ctx.stroke();
    }
  }

  /**
   * Draw a line between two points
   */
  drawLine(
    start: Vec2,
    end: Vec2,
    color: string,
    width: number = 2,
    glow: boolean = true
  ): void {
    const ctx = this.ctx;

    if (glow && this.glowEnabled) {
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = this.glowIntensity;
    }

    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.lineCap = 'round';

    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();

    if (glow && this.glowEnabled) {
      ctx.restore();
    }
  }

  /**
   * Draw a circle outline
   */
  drawCircle(
    center: Vec2,
    radius: number,
    color: string,
    lineWidth: number = 2,
    glow: boolean = true
  ): void {
    const ctx = this.ctx;

    if (glow && this.glowEnabled) {
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = this.glowIntensity;
    }

    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;

    ctx.beginPath();
    ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
    ctx.stroke();

    if (glow && this.glowEnabled) {
      ctx.restore();
    }
  }

  /**
   * Draw a filled circle
   */
  drawCircleFilled(
    center: Vec2,
    radius: number,
    color: string,
    glow: boolean = false
  ): void {
    const ctx = this.ctx;

    if (glow && this.glowEnabled) {
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = this.glowIntensity;
    }

    ctx.fillStyle = color;

    ctx.beginPath();
    ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
    ctx.fill();

    if (glow && this.glowEnabled) {
      ctx.restore();
    }
  }

  /**
   * Draw a polygon outline from points
   */
  drawPolygon(
    points: Vec2[],
    color: string,
    lineWidth: number = 2,
    closed: boolean = true,
    glow: boolean = true
  ): void {
    if (points.length < 2) return;

    const ctx = this.ctx;

    if (glow && this.glowEnabled) {
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = this.glowIntensity;
    }

    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);

    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }

    if (closed) {
      ctx.closePath();
    }

    ctx.stroke();

    if (glow && this.glowEnabled) {
      ctx.restore();
    }
  }

  /**
   * Draw a filled polygon
   */
  drawPolygonFilled(points: Vec2[], color: string, glow: boolean = false): void {
    if (points.length < 3) return;

    const ctx = this.ctx;

    if (glow && this.glowEnabled) {
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = this.glowIntensity;
    }

    ctx.fillStyle = color;

    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);

    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }

    ctx.closePath();
    ctx.fill();

    if (glow && this.glowEnabled) {
      ctx.restore();
    }
  }

  /**
   * Draw a rectangle outline
   */
  drawRect(
    pos: Vec2,
    width: number,
    height: number,
    color: string,
    lineWidth: number = 2,
    glow: boolean = true
  ): void {
    const ctx = this.ctx;

    if (glow && this.glowEnabled) {
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = this.glowIntensity;
    }

    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.strokeRect(pos.x, pos.y, width, height);

    if (glow && this.glowEnabled) {
      ctx.restore();
    }
  }

  /**
   * Draw a filled rectangle
   */
  drawRectFilled(
    pos: Vec2,
    width: number,
    height: number,
    color: string,
    glow: boolean = false
  ): void {
    const ctx = this.ctx;

    if (glow && this.glowEnabled) {
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = this.glowIntensity;
    }

    ctx.fillStyle = color;
    ctx.fillRect(pos.x, pos.y, width, height);

    if (glow && this.glowEnabled) {
      ctx.restore();
    }
  }

  /**
   * Draw text
   */
  drawText(
    text: string,
    pos: Vec2,
    color: string,
    size: number = 16,
    align: CanvasTextAlign = 'left',
    glow: boolean = true
  ): void {
    const ctx = this.ctx;

    if (glow && this.glowEnabled) {
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = this.glowIntensity / 2;
    }

    ctx.fillStyle = color;
    ctx.font = `${size}px "Courier New", monospace`;
    ctx.textAlign = align;
    ctx.textBaseline = 'top';
    ctx.fillText(text, pos.x, pos.y);

    if (glow && this.glowEnabled) {
      ctx.restore();
    }
  }

  /**
   * Draw a ship shape - 8 unique designs based on playerId
   * damageLevel (0-3) causes vertices to separate outward
   */
  drawShip(
    pos: Vec2,
    rotation: number,
    size: number,
    color: string,
    playerId: number = 0,
    fillColor?: string,
    damageLevel: number = 0
  ): void {
    let points = this.getShipShape(playerId, pos, rotation, size);

    // Apply damage expansion - vertices separate outward from center
    if (damageLevel > 0) {
      const expansionFactor = 1 + damageLevel * 0.15; // 15% expansion per damage level
      points = points.map((point) => {
        const offset = point.sub(pos);
        return pos.add(offset.scale(expansionFactor));
      });
    }

    if (fillColor) {
      this.drawPolygonFilled(points, fillColor);
    }
    this.drawPolygon(points, color, 2, true, true);
  }

  /**
   * Get ship shape points based on player ID (0-7)
   */
  private getShipShape(playerId: number, pos: Vec2, rot: number, size: number): Vec2[] {
    const shapeId = playerId % 8;

    switch (shapeId) {
      case 0: // Classic Arrow - sharp and fast looking
        return [
          pos.add(Vec2.fromAngle(rot).scale(size * 1.1)),
          pos.add(Vec2.fromAngle(rot + Math.PI * 0.85).scale(size * 0.7)),
          pos.add(Vec2.fromAngle(rot + Math.PI).scale(size * 0.3)),
          pos.add(Vec2.fromAngle(rot - Math.PI * 0.85).scale(size * 0.7)),
        ];

      case 1: // Diamond - wide and angular
        return [
          pos.add(Vec2.fromAngle(rot).scale(size)),
          pos.add(Vec2.fromAngle(rot + Math.PI * 0.5).scale(size * 0.6)),
          pos.add(Vec2.fromAngle(rot + Math.PI).scale(size * 0.8)),
          pos.add(Vec2.fromAngle(rot - Math.PI * 0.5).scale(size * 0.6)),
        ];

      case 2: // Dart - sleek with swept wings
        return [
          pos.add(Vec2.fromAngle(rot).scale(size * 1.2)),
          pos.add(Vec2.fromAngle(rot + Math.PI * 0.7).scale(size * 0.9)),
          pos.add(Vec2.fromAngle(rot + Math.PI * 0.9).scale(size * 0.4)),
          pos.add(Vec2.fromAngle(rot - Math.PI * 0.9).scale(size * 0.4)),
          pos.add(Vec2.fromAngle(rot - Math.PI * 0.7).scale(size * 0.9)),
        ];

      case 3: // Wedge - thick and heavy
        return [
          pos.add(Vec2.fromAngle(rot).scale(size * 0.9)),
          pos.add(Vec2.fromAngle(rot + Math.PI * 0.6).scale(size * 0.85)),
          pos.add(Vec2.fromAngle(rot + Math.PI).scale(size * 0.7)),
          pos.add(Vec2.fromAngle(rot - Math.PI * 0.6).scale(size * 0.85)),
        ];

      case 4: // Needle - long and thin
        return [
          pos.add(Vec2.fromAngle(rot).scale(size * 1.3)),
          pos.add(Vec2.fromAngle(rot + Math.PI * 0.8).scale(size * 0.5)),
          pos.add(Vec2.fromAngle(rot + Math.PI).scale(size * 0.6)),
          pos.add(Vec2.fromAngle(rot - Math.PI * 0.8).scale(size * 0.5)),
        ];

      case 5: // Chevron - V-shaped
        return [
          pos.add(Vec2.fromAngle(rot).scale(size)),
          pos.add(Vec2.fromAngle(rot + Math.PI * 0.65).scale(size * 0.95)),
          pos.add(Vec2.fromAngle(rot + Math.PI).scale(size * 0.2)),
          pos.add(Vec2.fromAngle(rot - Math.PI * 0.65).scale(size * 0.95)),
        ];

      case 6: // Hawk - curved wings
        return [
          pos.add(Vec2.fromAngle(rot).scale(size * 1.1)),
          pos.add(Vec2.fromAngle(rot + Math.PI * 0.55).scale(size * 0.75)),
          pos.add(Vec2.fromAngle(rot + Math.PI * 0.8).scale(size * 0.9)),
          pos.add(Vec2.fromAngle(rot + Math.PI).scale(size * 0.35)),
          pos.add(Vec2.fromAngle(rot - Math.PI * 0.8).scale(size * 0.9)),
          pos.add(Vec2.fromAngle(rot - Math.PI * 0.55).scale(size * 0.75)),
        ];

      case 7: // Stealth - angular faceted
      default:
        return [
          pos.add(Vec2.fromAngle(rot).scale(size)),
          pos.add(Vec2.fromAngle(rot + Math.PI * 0.4).scale(size * 0.5)),
          pos.add(Vec2.fromAngle(rot + Math.PI * 0.75).scale(size * 0.85)),
          pos.add(Vec2.fromAngle(rot + Math.PI).scale(size * 0.5)),
          pos.add(Vec2.fromAngle(rot - Math.PI * 0.75).scale(size * 0.85)),
          pos.add(Vec2.fromAngle(rot - Math.PI * 0.4).scale(size * 0.5)),
        ];
    }
  }

  /**
   * Draw an asteroid (irregular polygon)
   */
  drawAsteroid(
    pos: Vec2,
    radius: number,
    rotation: number,
    color: string,
    outlineColor: string,
    seed: number = 0
  ): void {
    const points: Vec2[] = [];
    const numPoints = 8;

    // Use seed to generate consistent "random" vertices
    for (let i = 0; i < numPoints; i++) {
      const angle = rotation + (i / numPoints) * Math.PI * 2;
      // Vary radius based on seed and index
      const variance = 0.7 + 0.3 * Math.sin(seed * 12.9898 + i * 78.233);
      const r = radius * variance;
      points.push(pos.add(Vec2.fromAngle(angle).scale(r)));
    }

    this.drawPolygonFilled(points, color);
    this.drawPolygon(points, outlineColor, 2, true, true);
  }

  /**
   * Draw a crate (glowing box)
   */
  drawCrate(pos: Vec2, size: number, color: string, outlineColor: string): void {
    const half = size / 2;
    const rectPos = new Vec2(pos.x - half, pos.y - half);

    this.drawRectFilled(rectPos, size, size, color, true);
    this.drawRect(rectPos, size, size, outlineColor, 2, true);
  }

  /**
   * Draw a projectile (glowing line)
   */
  drawProjectile(pos: Vec2, vel: Vec2, color: string, length: number = 15): void {
    const dir = vel.normalize();
    const end = pos.sub(dir.scale(length));

    // Draw trail with fade
    const ctx = this.ctx;
    ctx.save();
    ctx.shadowColor = color;
    ctx.shadowBlur = this.glowIntensity * 1.5;

    const gradient = ctx.createLinearGradient(pos.x, pos.y, end.x, end.y);
    gradient.addColorStop(0, color);
    gradient.addColorStop(1, 'transparent');

    ctx.strokeStyle = gradient;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';

    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();

    ctx.restore();

    // Draw bright tip
    this.drawCircleFilled(pos, 3, color, true);
  }

  /**
   * Draw the station (hexagonal structure)
   */
  drawStation(pos: Vec2, radius: number, color: string, outlineColor: string): void {
    const points: Vec2[] = [];
    const numPoints = 6;

    for (let i = 0; i < numPoints; i++) {
      const angle = (i / numPoints) * Math.PI * 2 - Math.PI / 2;
      points.push(pos.add(Vec2.fromAngle(angle).scale(radius)));
    }

    // Draw inner filled hexagon
    this.drawPolygonFilled(points, color + '40'); // With alpha
    this.drawPolygon(points, outlineColor, 3, true, true);

    // Draw outer ring
    this.drawCircle(pos, radius * 1.3, outlineColor, 1, true);
  }

  /**
   * Set glow enabled/disabled
   */
  setGlowEnabled(enabled: boolean): void {
    this.glowEnabled = enabled;
  }

  /**
   * Set glow intensity
   */
  setGlowIntensity(intensity: number): void {
    this.glowIntensity = intensity;
  }

  /**
   * Get the raw canvas context for custom drawing
   */
  getContext(): CanvasRenderingContext2D {
    return this.ctx;
  }

  /**
   * Draw a particle based on its type
   */
  drawParticle(
    pos: Vec2,
    size: number,
    color: string,
    type: 'spark' | 'debris' | 'trail' | 'ring' | 'line' | 'fractal',
    alpha: number,
    rotation?: number,
    endOffset?: Vec2
  ): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = alpha;
    switch (type) {
      case 'spark':
        ctx.shadowColor = color;
        ctx.shadowBlur = this.glowIntensity;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, size, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'debris':
        ctx.translate(pos.x, pos.y);
        if (rotation) ctx.rotate(rotation);
        ctx.fillStyle = color;
        ctx.fillRect(-size / 2, -size / 2, size, size);
        break;
      case 'trail':
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, size, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'ring':
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.shadowColor = color;
        ctx.shadowBlur = this.glowIntensity;
        const ringSize = size * (2 - alpha);
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, ringSize, 0, Math.PI * 2);
        ctx.stroke();
        break;
      case 'line':
        // Flying line debris from ship explosion
        if (endOffset) {
          ctx.translate(pos.x, pos.y);
          if (rotation) ctx.rotate(rotation);
          ctx.strokeStyle = color;
          ctx.lineWidth = size;
          ctx.lineCap = 'round';
          ctx.shadowColor = color;
          ctx.shadowBlur = this.glowIntensity;
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(endOffset.x, endOffset.y);
          ctx.stroke();
        }
        break;
      case 'fractal':
        // Fractal/crystalline respawn effect lines
        if (endOffset) {
          ctx.strokeStyle = color;
          ctx.lineWidth = size;
          ctx.lineCap = 'round';
          ctx.shadowColor = color;
          ctx.shadowBlur = this.glowIntensity * 1.5; // Extra glow for respawn
          ctx.beginPath();
          ctx.moveTo(pos.x, pos.y);
          ctx.lineTo(pos.x + endOffset.x, pos.y + endOffset.y);
          ctx.stroke();
        }
        break;
    }
    ctx.restore();
  }

  /**
   * Draw a score popup text
   */
  drawPopup(pos: Vec2, text: string, color: string, alpha: number): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.shadowColor = color;
    ctx.shadowBlur = this.glowIntensity;
    ctx.fillStyle = color;
    ctx.font = '16px "Courier New", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, pos.x, pos.y);
    ctx.restore();
  }

  /**
   * Apply screen shake offset
   */
  applyShake(offsetX: number, offsetY: number): void {
    this.ctx.translate(offsetX, offsetY);
  }
}
